import React, { useId } from "react";
import { AlertCircle, CheckCircle2, Loader2, RotateCcw, UploadCloud, X } from "lucide-react";

import { cn } from "../../../utils/cn";

export type UploadStatus = "idle" | "uploading" | "uploaded" | "error";

type Props = {
  label: string;
  hint: string;
  file: File | null;
  status: UploadStatus;
  error?: string;
  onSelect: (file: File) => void;
  onClear: () => void;
  onRetry: () => void;
};

const DocumentUploadField = ({
  label,
  hint,
  file,
  status,
  error,
  onSelect,
  onClear,
  onRetry,
}: Props) => {
  // useId guarantees a unique input id per field. All three fields previously
  // hard-coded id="image-upload", so every <label htmlFor="image-upload">
  // resolved to the *first* input in the document — meaning the proof-of-address
  // and CAC buttons both opened the identity file picker.
  const inputId = useId();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) onSelect(selected);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
  };

  return (
    <div className="flex w-full flex-col">
      <h4 className="text-[14px] font-medium text-foreground">{label}</h4>
      <p className="mt-1 text-[11px] font-medium text-muted-foreground">{hint}</p>

      {file ? (
        <div
          className={cn(
            "mt-2 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors",
            status === "uploaded" && "border-success/40 bg-success/5",
            status === "error" && "border-destructive/40 bg-destructive/5",
            status === "uploading" && "border-input bg-muted/50"
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {status === "uploading" && (
              <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
            )}
            {status === "uploaded" && (
              <CheckCircle2 className="size-4 shrink-0 text-success" />
            )}
            {status === "error" && (
              <AlertCircle className="size-4 shrink-0 text-destructive" />
            )}
            <div className="min-w-0">
              <p className="truncate text-[13px] font-medium text-foreground">
                {file.name}
              </p>
              <p
                className={cn(
                  "text-[11px] font-medium",
                  status === "error" ? "text-destructive" : "text-muted-foreground"
                )}
              >
                {status === "uploading" && "Uploading…"}
                {status === "uploaded" && "Uploaded"}
                {status === "error" && (error || "Upload failed")}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {status === "error" && (
              <button
                type="button"
                onClick={onRetry}
                aria-label={`Retry uploading ${label}`}
                className="rounded-md p-1.5 text-primary transition-colors hover:bg-accent"
              >
                <RotateCcw className="size-4" />
              </button>
            )}
            {status !== "uploading" && (
              <button
                type="button"
                onClick={onClear}
                aria-label={`Remove ${label}`}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="mt-2 flex h-[45px] w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-input bg-muted/50 text-[14px] font-medium text-primary transition-colors hover:bg-muted"
        >
          <UploadCloud className="size-4" />
          Choose a file to upload
          <input
            id={inputId}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleChange}
            className="sr-only"
          />
        </label>
      )}
    </div>
  );
};

export default DocumentUploadField;
